import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { SoManagerVariogramPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { UPDATE_VARIOGRAM } from '~/components/upload/supporting-object/so-operations';
import { useZodForm } from '~/utils/forms';
import {
  initialVariogram,
  variogramSchema,
} from '~/utils/modules/supporting-object';

type Props = SOParent & {
  variogram: SoManagerVariogramPartsFragment;
  onUpdateSuccess?: () => unknown;
};

export function VariogramUpdateForm({
  variogram,
  studyId,
  onUpdateSuccess,
}: Props) {
  const [updateVariogram, { loading, error }] = useMutation(UPDATE_VARIOGRAM);

  const form = useZodForm({
    schema: variogramSchema,
    values: initialVariogram(variogram),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateVariogram({
        variables: {
          id: variogram.id,
          input: values,
        },
      });
      toast.success('Variogram updated successfully.');
      form.reset();
      if (onUpdateSuccess) onUpdateSuccess();
    } catch (err) {
      console.log('Error updating variogram', err);
      toast.error('There was a problem updating the variogram.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="VARIOGRAM" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
